import { EmptyPlaceholder } from '@automata/ui';
import { isTest, tracking } from '@automata/utils';
import type { Option } from '@automata/workflow-builder';
import { SxProps, Theme } from '@mui/material/styles';
import {
  DataGridPro,
  DataGridProProps,
  GridApiPro,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useRouter } from 'next/router';
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useURLFilters } from 'hooks/useURLFilters';
import { useURLSearchParams } from 'hooks/useURLSearchParams';

import { _accessColumnIds } from './columns';
import FilterPanel from './components/FilterPanel';
import Toolbar from './components/Toolbar';
import { getAvailableFilters, getSortModel, verifyRows } from './services';
import { Column, Row } from './types';

export type FilterPanelExtras = {
  typeOptions?: string[];
  tagOptions?: Option[];
};

const getDynamicStyles = (
  verticalAlign?: 'top' | 'center',
  disableSelectionOnClick?: boolean
): Record<string, SxProps<Theme>> => {
  return {
    dataGrid: {
      flexBasis: 0,
      '& .MuiDataGrid-cell': {
        alignItems: verticalAlign === 'top' ? 'flex-start' : 'center',
      },
      '& .MuiDataGrid-cell:hover': {
        color: disableSelectionOnClick ? 'inherit' : 'primary.main',
      },
      '& .MuiDataGrid-row:hover': {
        backgroundColor: disableSelectionOnClick ? 'transparent' : '#0000000A',
      },
      '& .hideRightSeparator .MuiDataGrid-columnSeparator--sideRight': {
        visibility: 'hidden',
      },
      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
        outline: 'none',
      },
      '& .MuiDataGrid-row': {
        cursor: disableSelectionOnClick ? 'default' : 'pointer',
        backgroundColor: 'transparent',
      },
    },
  };
};

const getSafeApiRef = (
  ref: RefObject<GridApiPro>
): MutableRefObject<GridApiPro> | undefined => {
  if (ref.current === null) return undefined;
  return ref as MutableRefObject<GridApiPro>;
};

type Data<RowShape extends object> = Omit<
  DataGridProProps,
  'columns' | 'rows'
> & {
  columns: Column<RowShape>[];
  rows: Row<RowShape>[];
};

export interface ModelListProps<RowShape extends object = any> {
  data: Data<RowShape>;
  resourceName: string;
  customEmptyText?: string;
  route?: string;
  filterMode?: DataGridProProps['filterMode'];
  disableToolbar?: boolean;
  disableSearch?: boolean;
  enableFilterByTag?: boolean;
  customHandleRowClick?: DataGridProProps['onRowClick'];
  hideNoResourceGraphic?: boolean;
  verticalAlign?: 'top' | 'center';
  sortableColumns?: Column[];
  filterPanelExtras?: FilterPanelExtras;
}

export const ModelList = <RowShape extends object = any>({
  data,
  resourceName,
  customEmptyText,
  route,
  customHandleRowClick,
  filterMode = 'client',
  disableToolbar = false,
  disableSearch = false,
  enableFilterByTag = false,
  verticalAlign,
  hideNoResourceGraphic = false,
  sortableColumns,
  filterPanelExtras,
}: ModelListProps<RowShape>): JSX.Element => {
  const router = useRouter();
  const apiRef = useGridApiRef();
  const [pageSize, setPageSize] = useState(25);
  const isTestEnv = isTest();
  const { filters } = useURLFilters();
  const [searchParams, setQueryParams] = useURLSearchParams();
  const tagFilters = searchParams.tags;
  const typesFilters = searchParams.types;

  const styles: Record<string, SxProps<Theme>> = getDynamicStyles(
    verticalAlign,
    data.disableRowSelectionOnClick
  );

  // runtime check of row integrity as static checks
  // here do not reject extraneous keys
  verifyRows(data.rows);

  const handleRowClick: DataGridProProps['onRowClick'] = useCallback(
    ({ row }: GridRowParams<Row>) => {
      _accessColumnIds((columnIds) => {
        tracking.track('screens.row.select', { id: row[columnIds.ID] });

        if (row[columnIds.TOGGLE_EXPANSION]) {
          const rowNode =
            apiRef && apiRef.current?.getRowNode(row[columnIds.ID]);
          const isGroupType = rowNode?.type === 'group';
          const childrenExpanded =
            (isGroupType && rowNode.childrenExpanded) ?? false;
          apiRef.current?.setRowChildrenExpansion(
            row[columnIds.ID],
            !childrenExpanded
          );
        } else if (row[columnIds.CUSTOM_ROUTE]) {
          router.push(row[columnIds.CUSTOM_ROUTE]);
        } else if (route) {
          router.push(`${route}/${row[columnIds.ID]}`);
        }
      });
    },
    [apiRef, router, route]
  );

  const availableFilters = useMemo(
    () => getAvailableFilters(data.columns),
    [data.columns]
  );

  const filterModel: DataGridProProps['filterModel'] = useMemo(
    () =>
      availableFilters.length
        ? {
            linkOperator: GridLogicOperator.And,
            items: filters,
          }
        : undefined,
    [availableFilters.length, filters]
  );

  const sortModel: GridSortModel | undefined = useMemo(
    () => getSortModel(data.columns),
    [data.columns]
  );

  const handleSortChange = (sm: GridSortModel) => {
    if (sm[0]) {
      apiRef.current?.setSortModel?.(sm);
      setQueryParams({ sortBy: sm[0].field });
    }
  };

  const noResourcesExist = useMemo(
    () =>
      !data.loading &&
      filters.length === 0 &&
      !tagFilters &&
      !typesFilters &&
      data.rows.length === 0,
    [data.loading, data.rows.length, filters.length, tagFilters, typesFilters]
  );

  const [panelTrigger, setPanelTrigger] = useState<'toolbar' | 'column' | null>(
    null
  );

  const [activeFilterField, setActiveFilterField] = useState<string | null>(
    null
  );

  useEffect(() => {
    type AnyApi = {
      subscribeEvent?: (
        event: string,
        handler: (params?: any) => void
      ) => () => void;
      showFilterPanel?: (args?: any) => void;
      hideFilterPanel?: () => void;
    };

    const api = apiRef.current as unknown as AnyApi;
    if (!api?.subscribeEvent) return;

    const onMenuOpen = (params?: any) => {
      setActiveFilterField(params?.field ?? null);
      setPanelTrigger('column');
    };

    const onShowPanel = (args?: any) => {
      const field = args?.targetColumnField ?? null;
      if (field) {
        setActiveFilterField(field);
        setPanelTrigger('column');
      } else {
        setActiveFilterField(null);
        setPanelTrigger('toolbar');
      }
    };

    const onHidePanel = () => {
      setActiveFilterField(null);
      setPanelTrigger(null);
    };

    const u1 = api.subscribeEvent?.('columnMenuOpen', onMenuOpen);
    const u2 = api.subscribeEvent?.('showFilterPanel', onShowPanel);
    const u3 = api.subscribeEvent?.('hideFilterPanel', onHidePanel);

    const originalShow = api.showFilterPanel?.bind(api);
    api.showFilterPanel = (args?: any) => {
      onShowPanel(args);
      return originalShow?.(args);
    };

    const originalHide = api.hideFilterPanel?.bind(api);
    api.hideFilterPanel = () => {
      onHidePanel();
      return originalHide?.();
    };

    return () => {
      u1?.();
      u2?.();
      u3?.();
      if (originalShow) api.showFilterPanel = originalShow;
      if (originalHide) api.hideFilterPanel = originalHide;
    };
  }, [apiRef]);

  useEffect(() => {
    if (searchParams.sortBy) {
      apiRef.current?.setSortModel?.([
        {
          field: searchParams.sortBy,
          sort: searchParams.sortBy === 'name' ? 'asc' : 'desc',
        },
      ]);
    } else if (sortModel) {
      apiRef.current?.setSortModel?.(sortModel);
    }
  }, [apiRef, searchParams.sortBy, sortModel]);

  if (noResourcesExist && !hideNoResourceGraphic) {
    return (
      <EmptyPlaceholder
        alt={`empty ${resourceName}`}
        title={`No ${resourceName} exist yet`}
        subTitle={customEmptyText ?? 'Click the blue + button to start'}
      />
    );
  }

  return (
    <DataGridPro<RowShape>
      disableVirtualization={isTestEnv}
      apiRef={getSafeApiRef(apiRef)}
      sx={styles.dataGrid}
      pageSizeOptions={[25, 50, 100]}
      pagination={data.pagination ?? true}
      onPaginationModelChange={({ pageSize }) => setPageSize(pageSize)}
      onRowClick={customHandleRowClick ?? handleRowClick}
      filterMode={filterMode}
      filterModel={filterModel}
      slots={{
        toolbar: disableToolbar ? undefined : Toolbar,
        filterPanel: FilterPanel,
        columnHeaderFilterIconButton: () => null,
      }}
      slotProps={{
        toolbar: {
          availableFilters,
          disableSearch,
          enableFilterByTag,
          sortFilter: sortableColumns
            ? { sortableColumns, handleSortChange }
            : undefined,
          defaultSort: searchParams.sortBy ?? undefined,
        },
        filterPanel: {
          availableFilters,
          ...(filterPanelExtras ?? {}),
          activeField: activeFilterField,
          panelTrigger,
        },
      }}
      hideFooter={data.hideFooter}
      disableRowSelectionOnClick
      aria-label={`${resourceName} list`}
      {...data}
      initialState={{
        ...data.initialState,
        pagination: {
          ...data.initialState?.pagination,
          paginationModel: {
            ...data.initialState?.pagination?.paginationModel,
            pageSize,
          },
        },
      }}
    />
  );
};

export default ModelList;
